/**
 * @fileoverview Repository for TareaOrden collection operations
 * Implements repository pattern for task queue percentage fix functionality
 */

import { DataRepository } from './base/DataRepository';
import { 
  TareaOrden, 
  CreateTareaOrdenData, 
  UpdateTareaOrdenData,
  TAREA_ORDEN_COLLECTION 
} from '../types/collections/TareaOrden';

/**
 * Repository class for TareaOrden collection
 * Provides specialized methods for task queue percentage fix functionality
 */
export class TareaOrdenRepository extends DataRepository<TareaOrden> {
  constructor() {
    super(TAREA_ORDEN_COLLECTION.name);
  }

  /**
   * Get task order by ID
   * @param tareaOrdenId - ID of the task order
   * @returns Task order record
   */
  async getTaskOrder(tareaOrdenId: string): Promise<TareaOrden> {
    return this.getById(tareaOrdenId);
  }

  /**
   * Update task order completion percentage
   * @param tareaOrdenId - ID of the task order
   * @param newPercentage - New completion percentage (0-100)
   * @returns Updated task order record
   */
  async updateTaskOrderPercentage(tareaOrdenId: string, newPercentage: number): Promise<TareaOrden> {
    // Ensure percentage is within valid range
    const cappedPercentage = Math.max(0, Math.min(100, newPercentage));
    
    return this.update(tareaOrdenId, { 
      porcentaje: cappedPercentage 
    });
  }

  /**
   * Mark task order as completed
   * @param tareaOrdenId - ID of the task order
   * @returns Updated task order record
   */
  async completeTaskOrder(tareaOrdenId: string): Promise<TareaOrden> {
    const now = new Date().toISOString();
    return this.update(tareaOrdenId, {
      fin: now,
      estado: 'completada'
    });
  }

  /**
   * Create new task order
   * @param data - Task order data
   * @returns Created task order record
   */
  async createTaskOrder(data: CreateTareaOrdenData): Promise<TareaOrden> {
    return this.create(data);
  }

  /**
   * Update existing task order
   * @param tareaOrdenId - ID of the task order
   * @param data - Update data
   * @returns Updated task order record
   */
  async updateTaskOrder(tareaOrdenId: string, data: UpdateTareaOrdenData): Promise<TareaOrden> {
    return this.update(tareaOrdenId, data);
  }

  /**
   * Get all task orders for a production order
   * @param ordenProduccionId - Production order ID
   * @returns Array of task orders sorted by inicio
   */
  async getTaskOrdersForProductionOrder(ordenProduccionId: string): Promise<TareaOrden[]> {
    return this.getAll({
      filter: `orden_produccion = "${ordenProduccionId}"`,
      sort: 'inicio'
    });
  }

  /**
   * Get task orders by status
   * @param status - Task order status
   * @returns Array of task orders with the specified status
   */
  async getTaskOrdersByStatus(status: string): Promise<TareaOrden[]> {
    return this.getAll({
      filter: `estado = "${status}"`,
      sort: 'inicio'
    });
  }

  /**
   * Get active task orders (not completed)
   * @returns Array of active task orders
   */
  async getActiveTaskOrders(): Promise<TareaOrden[]> {
    return this.getAll({
      filter: 'fin = ""',
      sort: 'inicio'
    });
  }

  /**
   * Get completed task orders
   * @returns Array of completed task orders
   */
  async getCompletedTaskOrders(): Promise<TareaOrden[]> {
    return this.getAll({
      filter: 'fin != ""',
      sort: 'fin'
    });
  }

  /**
   * Get task orders by percentage range
   * @param minPercentage - Minimum percentage
   * @param maxPercentage - Maximum percentage
   * @returns Array of task orders in the percentage range
   */
  async getTaskOrdersByPercentageRange(
    minPercentage: number, 
    maxPercentage: number
  ): Promise<TareaOrden[]> {
    return this.getAll({
      filter: `porcentaje >= ${minPercentage} && porcentaje <= ${maxPercentage}`,
      sort: 'porcentaje'
    });
  }

  /**
   * Get task orders created within date range
   * @param startDate - Start date
   * @param endDate - End date
   * @returns Array of task orders created in the date range
   */
  async getTaskOrdersByDateRange(startDate: string, endDate: string): Promise<TareaOrden[]> {
    return this.getAll({
      filter: `created >= "${startDate}" && created <= "${endDate}"`,
      sort: 'created'
    });
  }
}












